export type CartLinePayload = {
  id: string | number;
  qty: number;
  tint_details?: unknown[];
  [key: string]: unknown;
};

export type SavedCartPayload = {
  cart_id: string;
  sale_order_id?: number;
  company_id?: number | false;
  branch_id?: number | false;
  vehicle_id?: number | false;
  appointment_slot_id?: number | false;
  services: CartLinePayload[];
  products: CartLinePayload[];
  amount_total?: number;
  vehicle?: any;
  appointment?: any;
};

type CartState = {
  cart_id?: unknown;
  company_id?: unknown;
  branch_id?: unknown;
  vehicle?: { id?: unknown } | null;
  appointment?: { id?: unknown } | null;
  services?: unknown[];
  products?: unknown[];
};

function optionalId(value: unknown): number | undefined {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

function normalizeLines(
  lines: unknown[],
  includeTintDetails: boolean
): CartLinePayload[] {
  return lines.flatMap((raw) => {
    if (raw == null || typeof raw !== "object") return [];

    const line = raw as Record<string, unknown>;
    if (line.id == null || line.id === "") return [];

    const quantity = Number(line.qty);
    const normalized: CartLinePayload = {
      id: line.id as string | number,
      qty: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
    };

    if (
      includeTintDetails &&
      Array.isArray(line.tint_details) &&
      line.tint_details.length > 0
    ) {
      normalized.tint_details = line.tint_details;
    }

    return [normalized];
  });
}

/** Build the exact payload accepted by POST `/v1/my/cart`. */
export function buildSavedCartPayload(state: CartState): SavedCartPayload | null {
  const cartId =
    typeof state.cart_id === "string" ? state.cart_id.trim() : "";
  if (!cartId) return null;

  const payload: SavedCartPayload = {
    cart_id: cartId,
    services: normalizeLines(state.services ?? [], true),
    products: normalizeLines(state.products ?? [], false),
  };

  const companyId = optionalId(state.company_id);
  const branchId = optionalId(state.branch_id);
  const vehicleId = optionalId(state.vehicle?.id);
  const appointmentSlotId = optionalId(state.appointment?.id);

  if (companyId) payload.company_id = companyId;
  if (branchId) payload.branch_id = branchId;
  if (vehicleId) payload.vehicle_id = vehicleId;
  if (appointmentSlotId) payload.appointment_slot_id = appointmentSlotId;

  return payload;
}

/** Odoo endpoints may return the cart directly or under `result`/`data`. */
export function extractSavedCart(payload: unknown): SavedCartPayload | null {
  if (payload == null || typeof payload !== "object") return null;

  const record = payload as Record<string, unknown>;
  if (record.result && typeof record.result === "object") return extractSavedCart(record.result);
  if (record.data && typeof record.data === "object") return extractSavedCart(record.data);
  const candidate = record;

  if (
    candidate == null ||
    typeof candidate !== "object" ||
    !(candidate as Record<string, unknown>).cart_id ||
    !Array.isArray(candidate.services) || !Array.isArray(candidate.products)
  ) {
    return null;
  }

  const cart = candidate as Record<string, unknown>;
  const normalizeServerLines = (lines: unknown[]) => lines.map((line: any) => {
    if (!line || typeof line !== "object" || line.id == null) return null;
    const qty = Number(line.qty ?? 1);
    return Number.isFinite(qty) && qty > 0 ? { ...line, qty } : null;
  });
  const services = normalizeServerLines(cart.services as unknown[]);
  const products = normalizeServerLines(cart.products as unknown[]);
  if (services.includes(null) || products.includes(null)) return null;
  return {
    ...(cart as SavedCartPayload),
    cart_id: String(cart.cart_id),
    services: services as CartLinePayload[],
    products: products as CartLinePayload[],
  };
}

/** Distinguish an empty server cart from an incomplete/malformed response. */
export function isEmptySavedCartResponse(payload: any): boolean {
  if (payload === null || payload === false) return true;
  if (!payload || typeof payload !== "object") return false;
  if ("result" in payload) return isEmptySavedCartResponse(payload.result);
  if ("data" in payload) return isEmptySavedCartResponse(payload.data);
  return payload.cart_id === false || payload.cart_id === null || payload.cart_id === "";
}
